import { postWithAuthentication, getWithAuthentication, deleteWithAuthentication } from './api'
import { BASE_URL, CHAT_SCENARIOS_GENERATE_ENDPOINT, CHAT_CONVERSATIONS_ENDPOINT } from '@/assets/constants/api_endpoints'
import {
  CHAT_MESSAGE_TYPE_USER_MESSAGE,
  CHAT_MESSAGE_TYPE_USER_MESSAGE_AUDIO,
  CHAT_MESSAGE_TYPE_USER_MESSAGE_TRANSCRIBED,
  CHAT_MESSAGE_TYPE_TRANSCRIPTION_FAILED,
  CHAT_MESSAGE_TYPE_AI_MESSAGE,
  CHAT_MESSAGE_TYPE_AI_MESSAGE_AUDIO,
  CHAT_MESSAGE_TYPE_TTS_FAILED,
  CHAT_MESSAGE_TYPE_SESSION_CREATED,
  CHAT_MESSAGE_TYPE_SESSION_END,
  CHAT_MESSAGE_TYPE_SCENARIO_START,
  CHAT_MESSAGE_TYPE_CORRECTIONS,
  CHAT_MESSAGE_TYPE_RAG_ANSWER_CHUNK,
  CHAT_MESSAGE_TYPE_ERROR
} from '@/assets/constants/constants'

/**
 * Generates scenario cards for the given subject.
 *
 * Makes an authenticated POST request to the backend scenarios endpoint.
 * Expects request body: { subject_id: number, language: string, force_regenerate?: boolean }
 * Returns the scenarios array from the response.
 *
 * @param {number} subjectId - The ID of the subject to generate scenarios for
 * @param {string} language - The language code (e.g., 'ja' for Japanese)
 * @param {boolean} forceRegenerate - Force generation of new scenarios (bypass cache)
 * @returns {Promise<Array>} Array of scenario objects with id, type, title, description, etc.
 */
export async function generateScenarios(subjectId, language = 'ja', forceRegenerate = false) {
  const response = await postWithAuthentication(BASE_URL, CHAT_SCENARIOS_GENERATE_ENDPOINT, {
    subject_id: subjectId,
    language: language,
    force_regenerate: forceRegenerate
  })
  // Extract scenarios array from nested response structure
  return response?.data?.scenarios || []
}

/**
 * Fetches list of past conversations for the current user.
 *
 * Makes an authenticated GET request to the conversations endpoint.
 * Returns an array of conversation objects with scenario details, character info, and summary.
 *
 * @returns {Promise<Array>} Array of conversation objects with id, session_id, scenario_title, character_name, level, summary, created_at, updated_at
 */
export async function fetchPastConversations() {
  const response = await getWithAuthentication(BASE_URL, CHAT_CONVERSATIONS_ENDPOINT)
  // Extract conversations array from nested response structure: { success: true, data: [...] }
  return response?.data?.data || []
}

/**
 * Deletes a conversation for the current user.
 *
 * Sends DELETE to the conversations endpoint with body { conversation_id: string }.
 *
 * @param {number|string} conversationId - The ID of the conversation to delete
 * @returns {Promise<void>}
 */
export async function deleteConversation(conversationId) {
  await deleteWithAuthentication(BASE_URL, CHAT_CONVERSATIONS_ENDPOINT + "/", {
    session_id: String(conversationId)
  })
}

/**
 * Message type classifications for routing and handling
 */
export const MESSAGE_CATEGORIES = {
  SESSION: [CHAT_MESSAGE_TYPE_SESSION_CREATED, CHAT_MESSAGE_TYPE_SESSION_END, CHAT_MESSAGE_TYPE_SCENARIO_START],
  USER_INPUT: [CHAT_MESSAGE_TYPE_USER_MESSAGE, CHAT_MESSAGE_TYPE_USER_MESSAGE_AUDIO],
  TRANSCRIPTION: [CHAT_MESSAGE_TYPE_USER_MESSAGE_TRANSCRIBED, CHAT_MESSAGE_TYPE_TRANSCRIPTION_FAILED],
  AI_RESPONSE: [CHAT_MESSAGE_TYPE_AI_MESSAGE, CHAT_MESSAGE_TYPE_CORRECTIONS, CHAT_MESSAGE_TYPE_RAG_ANSWER_CHUNK],
  AUDIO_OUTPUT: [CHAT_MESSAGE_TYPE_AI_MESSAGE_AUDIO, CHAT_MESSAGE_TYPE_TTS_FAILED],
  ERROR: [CHAT_MESSAGE_TYPE_ERROR]
}

/**
 * Parse WebSocket message and categorize it
 * @param {Object} message - Message object from WebSocket
 * @returns {Object} { type, category, data, isError }
 */
export function parseMessage(message) {
  const type = message?.type
  let category = 'unknown'

  for (const [cat, types] of Object.entries(MESSAGE_CATEGORIES)) {
    if (types.includes(type)) {
      category = cat
      break
    }
  }

  return {
    type,
    category,
    data: message?.data || message,
    isError: category === 'ERROR' || type === CHAT_MESSAGE_TYPE_TRANSCRIPTION_FAILED || type === CHAT_MESSAGE_TYPE_TTS_FAILED
  }
}

/**
 * Validate audio message payload
 * @param {Object} payload - Audio message payload
 * @returns {Object} { valid: boolean, error?: string }
 */
export function validateAudioMessage(payload) {
  if (!payload.audio_data || typeof payload.audio_data !== 'string') {
    return { valid: false, error: 'Invalid audio data format' }
  }

  if (!payload.duration_ms || payload.duration_ms <= 0 || payload.duration_ms > 30000) {
    return { valid: false, error: 'Invalid audio duration (must be 1-30000 ms)' }
  }

  return { valid: true }
}

/**
 * Validate presigned S3 URL from AI audio message
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export function isValidS3PresignedUrl(url) {
  if (!url || typeof url !== 'string') return false

  try {
    const urlObj = new URL(url)
    return (
      (urlObj.hostname.includes('s3') || urlObj.hostname.includes('amazonaws.com')) &&
      urlObj.searchParams.has('Signature')
    )
  } catch {
    return false
  }
}
